
import { editJobTicket, editJobArticle, createJobArticle, getJobTicketAttachment, getZammadChannelsEmail } from '@/api';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { format, utcToZonedTime } from 'date-fns-tz';
import { checkTicketIsRfc, cleanEmailString } from '@/utils/Common';
import { mapState } from 'vuex';
import { timeZoneDefault, notificationErrorText } from '@/utils/PortalConstants';

import Avatar from '@/components/UI/Avatar.vue';
import Icon from '@/components/UI/Icon.vue';
import Button from '@/components/UI/Button.vue';
import DropZone from '@/components/UI/DropZone.vue';
import Tooltip from '@/components/UI/Tooltip.vue';
import AttachmentsField from '@/components/fields/AttachmentsField.vue';
import UserInfoCard from '@/components/UI/UserInfoCard.vue';
import LookupMailbox from '@/components/UI/LookupMailbox.vue';

import { generateUserEmailName, extractEmails, validateEmails } from '@/utils/Common';
import { JobArticleData, UploadingFilesObjectForTable, User } from '@/models/main';
import {
  JobTicketResponce,
  JobTicketAtricle,
  JobTicketNewArticleData,
  JobTicketArticleFragmentsData,
} from '@/models/jobTicket';
import ZammadTicketImgPlaceholder from '@/components/jobDetailed/ZammadTicketImgPlaceholder.vue';
import store from '@/store';

import { sendError } from '@/helpers/sentry';
import { extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
import { fetchSetTicketState } from '@/helpers/fetchers';
import { generateEmailSignature } from '@/helpers/zammadEmailTemplates';

extend('required', {
  ...required,
  message: 'This field is required',
});

@Component({
  components: {
    Avatar,
    Button,
    DropZone,
    Icon,
    Tooltip,
    AttachmentsField,
    UserInfoCard,
    LookupMailbox,
    ZammadTicketImgPlaceholder,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class ZammadTicket extends Vue {
  @Prop({ type: Array, required: true }) public articlesData: JobTicketAtricle[];
  @Prop({ type: Object, required: false }) public ticketData: JobTicketResponce;
  @Prop({ type: Boolean, required: false }) public loadingRfcRequest: boolean;

  public user: User;
  public format = format;
  public utcToZonedTime = utcToZonedTime;
  public timeZoneDefault = timeZoneDefault;
  public openMessages: number[] = [];

  public checkTicketIsRfc = checkTicketIsRfc;
  private validateEmails = validateEmails;

  public loadingNewArticle = false;
  public newArticleData: JobTicketNewArticleData = {
    from: '',
    toChips: [],
    ccChips: [],
    subject: '',
    message: '',
    typeIsEmail: false,
    quote: '',
  };
  private channelsEmailList: string[] = [];

  public setChips(label: string, chips: string[]) {
    this.newArticleData[`${label}Chips`] = chips;
    this.validateEmailToInput();
  }

  public uploadError = '';
  private files: File[] = [];
  public filesObjectsForTable: UploadingFilesObjectForTable[] = [];

  public toInputHasError = false;

  // public showUserInfoId = -1;

  public validateEmailToInput() {
    if (this.newArticleData.typeIsEmail) {
      this.toInputHasError = !this.newArticleData.toChips.length;
    } else {
      this.toInputHasError = false;
    }
  }

  public msgTypeChanged(isEmail) {
    if (!isEmail) {
      this.newArticleData.quote = '';
      this.newArticleData.subject = '';
      this.newArticleData.from = '';
      this.newArticleData.toChips = [];
      this.newArticleData.ccChips = [];
      this.newArticleData.message = '';
    }
  }

  public replyClicked(article, all) {
    this.newArticleData.typeIsEmail = true;
    this.newArticleData.from = article.from;
    this.newArticleData.toChips = article.to ? extractEmails(article.to) : [];
    this.newArticleData.ccChips = all && article.cc ? extractEmails(article.cc) : [];
    this.newArticleData.subject = article.subject;

    const date = format(new Date(article.created_at), 'EEEE, LLLL d, yyyy');
    const time = format(new Date(article.created_at), 'hh:mm:ss aa');
    const dateSign = `On ${date} at ${time}, ${article.created_by} wrote`;

    this.newArticleData.quote = `<div><blockquote type='cite'>${dateSign}:<br><br/>${article.body}</blockquote></div><div></div>`;

    const modal = document.querySelector('.v-dialog.v-dialog--active');
    if (modal) {
      modal.scrollTo({ top: modal.scrollHeight, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
    }
  }

  public toggleMessageClicked(id: number): void {
    const articleId = this.openMessages.indexOf(id);
    if (articleId === -1) {
      this.openMessages.push(id);
    } else {
      this.openMessages.splice(articleId, 1);
    }
  }

  public async togglePrivacy(article: JobTicketAtricle): Promise<void> {
    const data: JobArticleData = {
      id: article.id,
      ticket_id: article.ticket_id,
      subject: article.subject,
      body: article.body,
      content_type: 'text/html',
      type: article.type,
      internal: !article.internal,
      sender: 'Customer',
      attachments: article.attachments,
    };
    await editJobArticle(data)
      .then(res => {
        const editingArticleIdx = this.articlesData.findIndex(x => x.id === article.id);
        if (editingArticleIdx >= 0) {
          this.articlesData[editingArticleIdx].internal = res.internal;
        }
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Error sending the message',
          text: notificationErrorText,
          type: 'warn',
        });
      });
  }

  public async sendArticle(): Promise<void> {
    const msgForm = this.$refs.msgForm as HTMLFormElement;
    if (msgForm) {
      this.validateEmailToInput();
      const success = await msgForm.validate();
      if (success && !this.toInputHasError) {
        this.loadingNewArticle = true;
        let body = this.newArticleData.message.replace(/\n/g, '<br>');
        // Add quote to body for emails
        if (this.newArticleData.typeIsEmail && this.newArticleData.quote) {
          body = body + '<br/><div><br/></div>' + this.newArticleData.quote;
        }
        const group = process.env.VUE_APP_SENTRY_ENVIRONMENT === 'production' ? 'Service Delivery Team' : 'IT Support';

        if (this.newArticleData.typeIsEmail && this.$ability.can('staff', 'Job')) {
          const signature = generateEmailSignature({
            fullName: generateUserEmailName(this.user),
            email: this.user.email || 'info@kkts.com.au',
            phone: this.user.phone_number || '(02) 9575-0000',
            group: group,
          });
          body = body + '<br/><br/>' + signature;
        }

        const data: JobArticleData = {
          ticket_id: this.articlesData[0].ticket_id,
          subject: this.newArticleData.subject,
          body: body,
          from: this.newArticleData.from,
          to: cleanEmailString(this.newArticleData.toChips, this.channelsEmailList).join(', '),
          cc: cleanEmailString(this.newArticleData.ccChips, this.channelsEmailList).join(', '),
          content_type: 'text/html',
          type: this.newArticleData.typeIsEmail ? 'email' : 'note',
          internal: this.newArticleData.typeIsEmail ? false : true,
          sender: this.$ability.can('staff', 'Job') ? 'Agent' : 'Customer',
          attachments: this.files,
        };
        createJobArticle(data)
          .then(async res => {
            if (
              (this.ticketData.state.toLowerCase() === 'new' && this.articlesData.length === 1) ||
              this.ticketData.state.toLowerCase() === 'closed'
            ) {
              await this.setTicketState('open', 2);
            }
            this.newArticleData.subject = '';
            this.newArticleData.message = '';
            this.newArticleData.toChips = [];
            this.newArticleData.ccChips = [];
            this.newArticleData.quote = '';
            this.files = [];
            this.filesObjectsForTable = [];
            this.uploadError = '';
            this.articlesData.push(res);
            msgForm.reset();
          })
          .catch(() => {
            this.$notify({
              group: 'network',
              title: 'Error sending the message',
              text: notificationErrorText,
              type: 'warn',
            });
          })
          .finally(() => {
            this.loadingNewArticle = false;
          });
      }
    }
  }

  // Attachments:
  public filesAdded(files: File[]): void {
    this.uploadError = '';
    this.filesObjectsForTable = [];
    this.files = [...this.files, ...files];
    if (this.files.length > 5) {
      this.uploadError = 'You are only allowed to upload a maximum of 5 files';
      this.files.splice(5);
    }
    this.files.map(file => {
      this.filesObjectsForTable.push({
        name: file.name,
        size: file.size,
      });
    });
  }

  public deleteFiles(item: UploadingFilesObjectForTable): void {
    const fileIdx = this.files.findIndex(x => x.name === item.name);
    if (fileIdx >= 0) {
      this.files.splice(fileIdx, 1);
      this.filesObjectsForTable.splice(fileIdx, 1);
      this.uploadError = '';
    }
  }

  public downloadImage(ticket_id: number, article_id: number, file_id: number, file_name: string): void {
    getJobTicketAttachment(ticket_id, article_id, file_id)
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file_name);
        document.body.appendChild(link);
        link.click();
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Error downloading the image',
          text: notificationErrorText,
          type: 'warn',
        });
      });
  }

  private async setTicketState(state: string, state_id: number) {
    await fetchSetTicketState(this.ticketData, state, state_id).then(res => {
      this.$emit('zammadListChanged', res);
    });
  }

  public async closeR4CClicked() {
    await this.setTicketState('closed', 4);
  }

  // Temporary hidden
  // User card on avatar hover
  // public avatarOver(id: number) {
  //   this.showUserInfoId = id;
  // }

  // public avatarLeave() {
  //   this.showUserInfoId = -1;
  // }

  private async getZammadChannelsEmailWrapper() {
    await getZammadChannelsEmail()
      .then(res => {
        this.channelsEmailList = res;
      })
      .catch(err => {
        sendError(err, 'ZammadTicket');
      });
  }

  /**
   * The function receives an "article" object as input and returns two arrays to display
   * the email body. The email body is parsed into chunks – the HTML code before and
   * after the <img /> tag is added to the fragments without changes. The content of
   * the <img /> tag is converted into an image object containing the necessary data
   * for the `ZammadTicketImgPlaceholder` component – an
   * image placeholder/loader for Zammad articles.
   * For the array with fragments for the preview, the <img /> tag is replaced
   * with a string containing the image name.
   */
  private generateArticleFragments(html) {
    const regex = /<img[^>]*?>/g;
    const fragments: JobTicketArticleFragmentsData[] = [];
    const fragmentsPreview: string[] = [];
    let lastIndex = 0;

    for (const match of html.body.matchAll(regex)) {
      const fragment = html.body.substring(lastIndex, match.index);
      fragments.push({ fragment: fragment });
      fragmentsPreview.push(fragment);
      lastIndex = match.index + match[0].length;
      const imgRegex = /<img[^>]+src="(\/[^">]+)"/g;

      const imgMatch = imgRegex.exec(match[0]);

      if (!imgMatch) {
        return;
      }

      const parts = imgMatch[1].split('/');
      const imageId = parts[parts.length - 1].substring(0, parts[parts.length - 1].indexOf('?'));

      const imageName = html.attachments.find(x => x.id === Number(imageId))?.filename;

      fragments.push({
        image: {
          id: Number(imageId),
          name: imageName,
          loading: false,
        },
      });
      fragmentsPreview.push(`*** Image: "${imageName}" ***</div>`);
    }

    fragments.push({ fragment: html.body.substring(lastIndex) });
    fragmentsPreview.push(html.body.substring(lastIndex));
    const fragmentsPreviewString = fragmentsPreview.join('').substring(0, 300);
    return {
      fragments: fragments,
      fragmentsPreview: fragmentsPreviewString,
    };
  }

  private mounted() {
    if (this.articlesData && this.articlesData[0]) {
      store.dispatch('moduleNotifications/setTicketNotificationsAsRead', this.articlesData[0].ticket_id);
    }
    this.getZammadChannelsEmailWrapper();
    // Create fragments for each article and write them to the article object
    this.articlesData.map(article => {
      const res = this.generateArticleFragments(article);
      if (res) {
        article.fragments = res['fragments'];
        article.fragmentsPreview = res['fragmentsPreview'];
        this.$forceUpdate();
      }
    });
  }
}
